import React from 'react';

// iconBg: explicit background for the icon chip/badge.
//   'white'  → logo uses dark fills on transparent; show on #fff so the marks are readable.
//   <hex>    → logo has white/light fills; use this solid color so those fills are visible.
const BANKS = [
  {
    key: 'akbank',
    brandColor: '#DC0005',
    iconBg: '#ffffff',
    names: ['akbank', 'ak bank'],
  },
  {
    key: 'denizbank',
    brandColor: '#00529a',
    iconBg: '#004c91',   // SVG: dark-blue + red + white fills → colored background
    names: ['denizbank', 'deniz bank'],
  },
  {
    key: 'garanti',
    brandColor: '#004481',
    iconBg: '#ffffff',
    names: ['garanti', 'garanti bbva', 'garanti bankası'],
  },
  {
    key: 'halkbank',
    brandColor: '#005697',
    iconBg: '#ffffff',
    names: ['halkbank', 'halk bank', 'halk bankası', 'halk bankasi', 'türkiye halk bankası'],
  },
  {
    key: 'isbankasi',
    brandColor: '#003B71',
    iconBg: '#ffffff',
    names: ['iş bankası', 'işbank', 'is bankasi', 'iş bank', 'türkiye iş bankası', 'turkiye is bankasi'],
  },
  {
    key: 'kuveytturk',
    brandColor: '#16a086',
    iconBg: '#16A085',   // SVG: green + white fills → brand green background
    names: ['kuveyt türk', 'kuveyt turk', 'kuveytturk', 'kuveyttürk'],
  },
  {
    key: 'qnbfinansbank',
    brandColor: '#870052',
    iconBg: '#870052',   // SVG: white + blue + dark fills → brand magenta background
    names: ['finansbank', 'finans bank', 'qnb', 'qnb finansbank'],
  },
  {
    key: 'sekbank',
    brandColor: '#007a3d',
    iconBg: '#ffffff',
    names: ['şekerbank', 'sekerbank', 'şeker bank', 'seker bank'],
  },
  {
    key: 'teb',
    brandColor: '#00915a',
    iconBg: '#00915a',   // SVG: white + near-black fills → brand green background
    names: ['teb', 'türk ekonomik bankası', 'turk ekonomi bankasi', 'türk ekonomi bankasi'],
  },
  {
    key: 'vakifbank',
    brandColor: '#fdb913',
    iconBg: '#0c3572',   // SVG: yellow + white fills; yellow bg hides yellow → use dark navy instead
    names: ['vakıfbank', 'vakıf bank', 'vakifbank', 'vakif bank', 'türkiye vakıflar bankası'],
  },
  {
    key: 'yapikredi',
    brandColor: '#004990',
    iconBg: '#ffffff',
    names: ['yapı kredi', 'yapı ve kredi', 'yapi kredi', 'yapi ve kredi', 'yapıkredi', 'yapikredi'],
  },
  {
    key: 'ziraat',
    brandColor: '#e10514',
    iconBg: '#ffffff',
    names: ['ziraat', 'ziraat bankası', 'ziraat bankasi', 't.c. ziraat bankası'],
  },
];

const normalize = (s) => {
  return (s || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/\s+/g, ' ')
    .trim();
};

export function matchBank(bankName) {
  if (!bankName) return null;
  const q = normalize(bankName);
  for (const b of BANKS) {
    for (const n of b.names) {
      if (q.includes(n) || n.includes(q)) return b;
    }
  }
  return null;
}

/** Brand accent color — used for card top-border, chart series color, etc. */
export function getBankColor(bankIcon) {
  if (!bankIcon) return null;
  return BANKS.find(b => b.key === bankIcon)?.brandColor || null;
}

/**
 * Background color for the icon chip/badge wrapper.
 * Returns white for dark-fill logos and a solid brand/dark color for logos
 * that contain white fills (which would be invisible on white).
 */
export function getBankIconBackground(bankIcon) {
  if (!bankIcon) return null;
  return BANKS.find(b => b.key === bankIcon)?.iconBg || null;
}

export function resolveCardColor(card) {
  if (card?.bankIcon) {
    const c = getBankColor(card.bankIcon);
    if (c) return c;
  }
  return card?.colorHex || '#5470c6';
}

export function BankIcon({ bankIcon, bankName, width = 72, className = '' }) {
  const key = bankIcon || matchBank(bankName)?.key;
  if (!key) return null;

  return (
    <img
      src={`${process.env.PUBLIC_URL}/assets/banks/${key}.svg`}
      alt={BANKS.find(b => b.key === key)?.names[0] || key}
      width={width}
      className={className}
      style={{ flexShrink: 0, objectFit: 'contain' }}
    />
  );
}

export default BANKS;
